// 認証関連の関数などをまとめた
// auth0を使っているわけではないが、auth0を参考にした
// https://github.com/auth0/auth0-react
// https://github.com/auth0/nextjs-auth0 => 特にこちら

// https://github.com/auth0/nextjs-auth0#comparison-with-the-auth0-react-sdk
// 要するに、ふつうのreactだとブラウザ内のcontextからしか通信が起きないのでtokenなどをブラウザのsession storage
// などに入れておけば良いけど、
// nextjsだと通信が起きるcontextが「ブラウザ内」と「サーバー(node.js)」の2つになるので、サーバにsession storageとかないので、
// cookieを使わないとできない

// nextjsにはAPIを書ける機能(https://nextjs.org/docs/api-routes/introduction)があるので、それを下記のように使っている
// TODO: ここにもうちょっと詳細に

import type { NextApiRequest, NextApiResponse, GetServerSideProps } from "next";
import { createContext, useContext } from "react";

// custom
import { MeFieldFragment } from "graphql/generated";
import buildSSRGraphqlClient from "lib/ssrGraphqlClient";
import { getSdk } from "graphql/ssr.generated";
import { xReferer, reportToAppsignal, fetchMe } from "lib/appsignal";
import { DEFAULT_LOCALE, LOCAL_STATIC_IP } from "lib/const";
import handleBadRequestError from "lib/handle_bad_request";
import { fetchDict } from "lib/dict";
import { mergeDict } from "lib/mergeDict";
import { isBcpTokenEntry } from "lib/bcpTokenLogin";
import { isNotAccessibleGraphqlError, notAccessiblePagePath } from "lib/notAccessibleError";

import { COOKIE_KEY } from "lib/auth-constants";
import { setCookie, getCookieOptions, getDeleteCookieOptions } from "lib/authCookie";

// nextjs APIでログインAPIを定義する関数
// TODO: クライアントIP取得ロジックはtenant-frontend/pages/api/graphql_proxy.tsxにも同様の実装がある
//       将来的に共通化を検討する
// NOTE: graphql_proxy.tsxではSSR（getServerSideProps）経由のリクエストも考慮してx-nitoel-forwarded-forヘッダーを
//       参照しているが、handleLoginはブラウザからの直接リクエストのみを受けるため、x-forwarded-forのみを参照する
async function handleLogin(req: NextApiRequest, res: NextApiResponse) {
  // TODO: 環境変数
  // ブラウザのUserAgentとIPアドレスをRailsに転送（ログイン履歴に記録するため）
  // 開発環境ではVercelがないのでX-Forwarded-Forヘッダーは常にundefinedになるため、固定IPを使用
  let clientIp = "";
  if (process.env.NODE_ENV === "development") {
    clientIp = LOCAL_STATIC_IP; // 開発環境では固定
  } else {
    clientIp = (req.headers["x-forwarded-for"] as string) || "";
  }

  const apiRes = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_API_BASE_URL}/supplier_accounts/sign_in`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // ブラウザのUserAgentを転送（ログイン履歴に記録するため）
      "User-Agent": req.headers["user-agent"] || "",
      // クライアントIPを転送（ログイン履歴に記録するため）
      "X-Forwarded-For": clientIp,
    },
    redirect: "manual",
    body: JSON.stringify(req.body),
  });

  if (apiRes.ok) {
    let apiResponseBody = {};
    if (apiRes.status === 204) {
      // 200 okでotp_requiredでない場合はrailsからのレスポンスに入ってるJWT tokenをsetCookieする
      // こうすることで後続のリクエストにcookie情報がcookieの仕様上勝手に入ってくる
      const authHeader = apiRes.headers.get("Authorization");
      const jwtToken = authHeader ? authHeader.replace("Bearer ", "") : null;
      setCookie(res, COOKIE_KEY, jwtToken, getCookieOptions());
    } else {
      // 2FAの認証前
      // 200 okで　message に "otp_required"　のレスポンスが返る
      apiResponseBody = await apiRes.json();
    }
    res.status(200).json(apiResponseBody);
    return;
  }
  if (apiRes.status === 422) {
    res.status(422).json(await apiRes.json().catch(() => ({})));
    return;
  }
  res.status(401).json({});
  return;
}

// react側から呼び出すlogout関数
// 返り値: promise
function logout() {
  return fetch("/api/auth/logout", {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });
}

// nextjs APIでログアウトAPIを定義する関数
// NOTE: バックエンド側のJWT無効化に失敗した場合でも、ブラウザのcookieは必ず削除する。
// cookieを残すとログアウト自体ができなくなり、共有PCで前の利用者のセッションが継続する
// （NITOEL-9508の事象そのもの）ため。無効化漏れはAppSignalで検知して個別に対応する。
async function handleLogout(req: NextApiRequest, res: NextApiResponse) {
  const jwtToken = req.cookies[COOKIE_KEY];

  try {
    // cookieが無ければ無効化する対象のJWTも無いので、バックエンドは呼ばない
    if (jwtToken) {
      let apiRes: Response | null = null;

      // try で囲むのは fetch だけにする。報告処理まで含めると、AppSignal 側の失敗を
      // 「バックエンドへ到達できなかった」として誤ったラベルで再報告してしまうため。
      try {
        apiRes = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_API_BASE_URL}/supplier_accounts/sign_out`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          redirect: "manual",
        });
      } catch (error) {
        // バックエンドへ到達できないケース。fetchMeも同じバックエンドを叩くのでここでは呼ばない
        reportToAppsignal(
          error instanceof Error ? error : new Error("Supplier logout backend request failed"),
          null,
          xReferer(req),
        );
      }

      if (apiRes && !apiRes.ok) {
        reportToAppsignal(
          new Error(`Supplier logout backend failed with status ${apiRes.status}`),
          await fetchMe(jwtToken),
          xReferer(req),
        );
      }
    }
  } catch (error) {
    // finally で 200 を返すため、ここで握り潰さないと「送信済みレスポンスに対して
    // Next.js が 500 を返そうとする」形になり ERR_HTTP_HEADERS_SENT になる
    console.error("[handleLogout] unexpected error while revoking the supplier session:", error);
  } finally {
    // AppSignalへの通知自体が失敗してもcookie削除には必ず到達させる（finallyにしている理由）
    // https://stackoverflow.com/questions/62101821/nextjs-api-routes-how-to-remove-a-cookie-from-header
    setCookie(res, COOKIE_KEY, "", getDeleteCookieOptions());
    res.status(200).json({});
  }
}

function sendResetPasswordMail(email: string) {
  return fetch(`${process.env.NEXT_PUBLIC_BACKEND_API_BASE_URL}/supplier_accounts/password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ supplier_account: { email } }),
  });
}

function updatePassword(token: string, password: string, passwordConfirmation: string) {
  return fetch(`${process.env.NEXT_PUBLIC_BACKEND_API_BASE_URL}/supplier_accounts/password`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      supplier_account: { password, password_confirmation: passwordConfirmation, reset_password_token: token },
    }),
  });
}

export const MeContext = createContext<MeFieldFragment | null>(null);
// component内で、現在ログイン中のuserを取得したいときに呼ぶ関数
// providerはpages/_app.tsxに書いてあり、初期値はpages配下のgetServerSidePropsなどが返すme props
export const useMe = () => useContext(MeContext);

// 認証が必要なページに噛ませる
// https://github.com/auth0/nextjs-auth0
// 上記auth0のnode moduleを参考にして作った
// 認証と同時にlocaleなども自動で設定してくれる
// 使用例: returnToを指定しなければ、そのページのurlが自動で入る
// export const getServerSideProps = withPageAuthRequired({
//   async getServerSideProps(ctx) {
//     return { props: { customProp: 'bar' } };
//   },
//   returnTo: '/foo',
// });
type WithPageAuthRequiredOptions = {
  getServerSideProps?: GetServerSideProps;
  returnTo?: string;
  trans?: {
    func: (locale: string, nameSpaces: string[]) => any;
    nameSpaces: string[];
  };
  dicts?: string[];
};

function withPageAuthRequired(options: WithPageAuthRequiredOptions): GetServerSideProps {
  const { getServerSideProps, returnTo, trans, dicts } = options;
  return async (context) => {
    try {
      const client = buildSSRGraphqlClient(context);
      const sdk = getSdk(client);
      const { me } = await sdk.Me();

      // 更新案内（lib/release）の文言は common にある。案内を出すのは配布工程で build した画面（NEXT_PUBLIC_RELEASE_BUILD_ID あり）
      // だけなので、そのときだけ common を足す。dicts を指定しないページは me の selectedTenant を使い、追加の問い合わせをしない
      const releaseNotice = Boolean(process.env.NEXT_PUBLIC_RELEASE_BUILD_ID);
      const normalizedDicts =
        dicts && (!releaseNotice || dicts.includes("common")) ? dicts : [...(dicts ?? []), ...(releaseNotice ? ["common"] : [])];
      let dict = {};
      if (normalizedDicts.length > 0) {
        const tenantId = dicts ? (await sdk.SelectedTenant()).selectedTenant?.id : me?.selectedTenant?.id;
        dict = await fetchDict(context, tenantId || "", normalizedDicts);
      }

      let ret: any = { props: {} };
      if (getServerSideProps) {
        ret = await getServerSideProps(context);
      }

      // ページが getServerSideProps で自分の辞書（es_quotation など）を返していれば、上書きせずにまとめる。
      // 上書きすると、更新案内のために common を取る画面（配布工程で build した画面）でページの辞書が消え、文言がキーのまま出る
      const dictProps = normalizedDicts.length > 0 ? { dict: mergeDict(dict, ret.props?.dict) } : {};

      if (trans) {
        const locale = me?.languageCode ? me.languageCode : DEFAULT_LOCALE;
        return {
          ...ret,
          props: {
            ...ret.props,
            ...(await trans.func(locale, trans.nameSpaces)),
            me,
            ...dictProps,
          },
        };
      } else {
        return {
          ...ret,
          props: {
            ...ret.props,
            me,
            ...dictProps,
          },
        };
      }
    } catch (error: any) {
      // ここに来るerrorは
      //   - backendで起きたerrorをgraphqlがobjectとして返すパターン
      //   - JSの文脈で起きた純粋なError
      // が想定される。前半3個のif分岐はrails側がgraphqlErrorとして返したものを補足している。
      // 一番最後でそれ以外のerrorを補足してappsignalに通知する。
      if (error.message.startsWith("unauthorized")) {
        const msg = "セッションが切れたのでログアウトしました";
        const target = returnTo || context.resolvedUrl;
        // ワンタイム URL の回答画面から来た未ログインの人にはセッション切れのトーストを出さない
        const notify = isBcpTokenEntry(target) ? "" : `&notify_msg=${encodeURIComponent(msg)}`;
        return {
          redirect: {
            destination: `/login?redirectTo=${encodeURIComponent(target)}${notify}`,
            permanent: false,
          },
        };
        // 権限系のエラー
      } else if (error.message.startsWith("permissionDeny")) {
        return {
          redirect: {
            destination: `/403?msg=${error.response?.errors[0].message}`,
            permanent: false,
          },
        };
        // token系のエラー諸々
      } else if (
        error.message.startsWith("invalid access") ||
        error.message.startsWith("already operated") ||
        error.message.startsWith("expired")
      ) {
        return {
          redirect: {
            destination: `/error/bad_request?error=${handleBadRequestError(error.response?.errors[0].message)}`,
            permanent: false,
          },
        };
      } else if (isNotAccessibleGraphqlError(error)) {
        return {
          redirect: {
            destination: notAccessiblePagePath(context.resolvedUrl),
            permanent: false,
          },
        };
      } else {
        reportToAppsignal(error as Error, await fetchMe(context.req.cookies[COOKIE_KEY]), xReferer(context.req));
        throw error;
      }
    }
  };
}

// react側から呼び出すlogin関数
async function login(email: string, password: string, otpAttempt = "", supplierCorporationId?: string) {
  const supplierAccount: Record<string, string | undefined> = {
    email,
    password,
    otpAttempt,
  };
  if (supplierCorporationId) {
    supplierAccount.supplier_corporation_id = supplierCorporationId;
  }
  const response = await fetch("/api/auth/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ supplier_account: supplierAccount }),
  });

  const data = await response.json().catch(() => ({}));
  return {
    ok: response.ok,
    data,
    status: response.status,
  };
}

function signupConnect(token: string, name: string, email: string, tel: string, password: string, fromKind: string) {
  return fetch("/api/auth/signup", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ supplier_account: { email, password, name, tel, token, from_kind: fromKind } }),
  });
}

// nextjs APIでサインアップAPIを定義する関数
async function handleSignUp(req: NextApiRequest, res: NextApiResponse) {
  // TODO: 環境変数
  const apiRes = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_API_BASE_URL}/supplier_accounts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    redirect: "manual",
    body: JSON.stringify(req.body),
  });

  if (apiRes.ok) {
    // 200 okならrailsからのレスポンスに入ってるJWT tokenをsetCookieする
    // こうすることで後続のリクエストにcookie情報がcookieの仕様上勝手に入ってくる
    const authHeader = apiRes.headers.get("Authorization");
    const jwtToken = authHeader ? authHeader.replace("Bearer ", "") : null;
    setCookie(res, COOKIE_KEY, jwtToken, getCookieOptions());
    res.status(200).json({});
    return;
  } else {
    // それ以外なら401で返すだけ
    const json = await apiRes.json();
    res.status(401).json(json);
    return;
  }
}

const exportedObject = {
  login,
  handleLogin,
  logout,
  handleLogout,
  sendResetPasswordMail,
  updatePassword,
  withPageAuthRequired,
  signupConnect,
  handleSignUp,
};
export default exportedObject;
